import { buttonUiTemplate } from "../frontViews/buttonUiTemplate";
import { itx } from "../../../src/translationItx/itx";
import { C1_ELO_LIGHT_TYPE_LABELS, C1_ELO_PLUG_TYPE_LABELS, C1_ELO_SENSOR_TYPE_LABELS, C1_ELO_VOLTAGE_LABELS, C1_WINDOW_SHADING_LABELS } from "../C1_uiLabels";
import { comparePdfLabels, pdfHeaderText } from "../../../src/pdf/pdfTagName";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Pencil, Sigma } from "lucide-react";
import c1_state from "../model/C1_State";
import type { C1_Room } from "../model/C1_Room";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_EloItem_Definition, C1_EloItem_Data } from "../types/C1_EloTypes";
import { C1_EloItem_PlugType, C1_EloItem_Type } from "../types/C1_EloTypes";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import { getEloItemProfileForInstance, getRoomEloItemProfile } from "../data/C1_eloItemsRegistry";
import C1_eloItemsRegistry from "../data/C1_eloItemsRegistry";
import C1_EloEquipmentEditor from "../dockingArea/C1_EloEquipmentEdior";
import type { C1_Furniture } from "../model/C1_Furniture";
import type { C1_Opening } from "../model/C1_Opening";
import { isLightGroupDefinitionId } from "../furniture/C1_lightGroup";
import { downloadC1RaumbuchPdf, type C1_RaumbuchPdfButton, type C1_RaumbuchPdfRoom } from "./C1_RaumbuchPdf";
import {
  createC1RoombookProjectSummary,
  getC1RoombookWiringCountExplanation,
  formatC1RoombookSummaryValue,
  hasC1RoombookSummaryWiringCounts,
  type C1_RoombookSummaryRow,
  visibleC1RoombookSummaryColumns,
} from "./C1_RaumbuchSummary";
import {
  C1_RaumbuchObjectDetails,
  c1RoombookObjectDetailsText,
  type C1_RaumbuchObject,
} from "./C1_RaumbuchObjectDetails";
import {
  createC1ProjectReportData,
  type C1_ReportButton,
  type C1_ReportConsumer,
  type C1_ReportFloor,
  type C1_ReportGroup,
  type C1_ReportRoom,
} from "../lib/C1_projectReportData";
import "./C1_Raumbuch.scss";
import { C1_RoombookViewModeSelect } from "./C1_RoombookViewModeSelect";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import C1_EloItem from "../model/C1_EloItem";
import { C1_Button } from "../model/C1_Button";
import { C1_Consumer } from "../model/C1_Consumer";
import { C1_ButtonView } from "../plan/C1_ButtonView";
import { getC1ButtonRows } from "../frontViews/C1_buttonRows";
import { uiModeWatcher } from "../../../src/uiMode";
import { compareTaggedLabels } from "../../../src/utils/tagSort";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { getButtonProductDescription, getLightProductDescription, getLightProductImageUrl } from "../data/C1_ProductDefinitions";
import { WallOutletSvg, type WallOutletSlotType } from "../frontViews/WallOutletFrontview";
import type Button from "../../../src/models/Button";
import { roombookButtonProductName } from "../../../src/pdf/roombookButtonProductName";
import { getChannelTypePicture } from "../../../src/models/ChannelType";

type Source = {
  id: string;
  title: string;
  masterIndex?: number;
  rootDefinitionId: string;
  definitions: C1_EloItem_Definition[];
  instance?: C1_EloItem_Data;
  object: C1_RaumbuchObject;
  onChange: (instance: C1_EloItem_Data) => void;
  onNameChange: (name?: string) => void;
};

const TYPE_LABELS: Record<C1_EloItem_Type, string> = {
  get [C1_EloItem_Type.IntermediateDistribution]() { return itx("channelType.IntermediateDistribution", "Unterverteiler"); },
  get [C1_EloItem_Type.Group]() { return itx("components.group", "Gruppe"); },
  get [C1_EloItem_Type.PlugGroup]() { return itx("components.plural.Outlet", "Steckdosen"); },
  get [C1_EloItem_Type.Blind]() { return itx("channelType.Blind", "Rollladen"); },
  get [C1_EloItem_Type.Jalousie]() { return itx("toolBar.jalousieShort", "Jalousie"); },
  get [C1_EloItem_Type.Light]() { return itx("switch.lighting", "Beleuchtung"); },
  get [C1_EloItem_Type.Plug]() { return itx("toolBar.outletShort", "Steckdose"); },
  get [C1_EloItem_Type.Button]() { return itx("menubar.buttons", "Taster"); },
  get [C1_EloItem_Type.Panel]() { return itx("c1.ui.Bedienpanel", "Bedienpanel"); },
  get [C1_EloItem_Type.FixedPower]() { return itx("c1.ui.Festanschluss", "Festanschluss"); },
  get [C1_EloItem_Type.DoorStation]() { return itx("c1.ui.Turkommunikation", "Türkommunikation"); },
  get [C1_EloItem_Type.WindowContact]() { return itx("toolBar.inputLock", "Kontakt"); },
  get [C1_EloItem_Type.Sensor]() { return itx("c1.ui.Sensorik", "Sensorik"); },
  get [C1_EloItem_Type.DoorLock]() { return itx("c1.ui.Turschloss", "Türschloss"); },
  get [C1_EloItem_Type.Thermostat]() { return itx("c1.ui.Thermostat", "Thermostat"); },
  get [C1_EloItem_Type.Heating]() { return itx("components.Heating", "Heizung"); },
  get [C1_EloItem_Type.Camera]() { return itx("c1.ui.Kamera", "Kamera"); },
};

function typeLabel(type: C1_EloItem_Type | "window_shading") {
  return type === "window_shading" ? itx("raumbuch.shade", "Beschattung") : TYPE_LABELS[type];
}

type RoombookItem = (C1_ReportConsumer | C1_ReportButton) & {
  itemType: C1_EloItem_Type | "window_shading";
};

type RoombookCategory = {
  id: string;
  title: string;
  items: RoombookItem[];
};

/** Reuses the classic consumer icons, including the model's DALI and channel-type variants. */
function roombookConsumerImage(item: RoombookItem): string | undefined {
  if (!("consumerType" in item) || item.groupDisplayOnly) return;
  const consumer = c1_state.objectRegistry.getObject<C1_Consumer>(item.id);
  if (consumer instanceof C1_Consumer) return consumer.consumer.image;
  const details = item.details;
  if (!details || !("channelType" in details) || !details.channelType) return;
  const image = getChannelTypePicture(details.channelType);
  return details.isDali ? image?.replace(/(\.[^.]+)$/, "_Dali$1") : image;
}

function compactLightGroupRows(items: RoombookItem[]): RoombookItem[] {
  const lightGroups = new Map<string, RoombookItem[]>();
  items.forEach((item) => {
    if (!item.sourceId || item.itemType !== C1_EloItem_Type.Light || item.groupDisplayOnly) return;
    const furniture = c1_state.project.floors
      .flatMap((floor) => floor.rooms)
      .flatMap((room) => room.furnitures)
      .find((candidate) => candidate.id === item.sourceId);
    if (!furniture || !isLightGroupDefinitionId(furniture.definitionId)) return;
    const groupItems = lightGroups.get(item.sourceId) ?? [];
    groupItems.push(item);
    lightGroups.set(item.sourceId, groupItems);
  });
  if (!lightGroups.size) return items;

  const emittedGroups = new Set<string>();
  return items.flatMap((item) => {
    const groupItems = item.sourceId ? lightGroups.get(item.sourceId) : undefined;
    if (!groupItems) return [item];
    if (emittedGroups.has(item.sourceId!)) return [];
    emittedGroups.add(item.sourceId!);
    const furniture = c1_state.project.floors
      .flatMap((floor) => floor.rooms)
      .flatMap((room) => room.furnitures)
      .find((candidate) => candidate.id === item.sourceId);
    const mountingName = furniture?.wallDocked ? itx("c1.ui.Wandleuchten", "Wandleuchten") : itx("c1.ui.Deckenleuchten", "Deckenleuchten");
    return [
      {
        ...groupItems[0],
        id: `${item.sourceId}-roombook-group`,
        tag: groupItems[0].tag,
        description: itx("c1.ui.value1_value2_Stuck", "{{value1}} {{value2}} Stück", { "{{value1}}": String(mountingName), "{{value2}}": String(groupItems.length) }),
        count: undefined,
        groupDisplayOnly: true,
        wiredTogetherWith: undefined,
      },
    ];
  });
}

function detailText(item: RoombookItem) {
  const details = item.details;
  const roomAssignment = item.belongsToRoomName ? itx("c1.ui.Gehort_zu_Raum_value1", "Gehört zu Raum {{value1}}", { "{{value1}}": String(item.belongsToRoomName) }) : "";
  if (item.itemType === C1_EloItem_Type.Button || item.itemType === C1_EloItem_Type.Panel) {
    const button = c1_state.objectRegistry.getObject<C1_Button>(item.id);
    const owner = button instanceof C1_Button ? button.owner as Button : undefined;
    const buttonType = button instanceof C1_Button ? buttonUiTemplate(button.owner?.baseProps.buttonBaseProps?.type) : undefined;
    const fallbackType = item.itemType === C1_EloItem_Type.Panel ? "panel" : "simple";
    const reportButtonType = details && "buttonType" in details ? details.buttonType : undefined;
    const productName = roombookButtonProductName(owner?.template?.name, buttonType ?? reportButtonType ?? fallbackType);
    return [roomAssignment, productName].filter(Boolean).join(" · ");
  }
  if (!details) return roomAssignment;
  if ("shading" in details) return C1_WINDOW_SHADING_LABELS[details.shading] ?? details.shading.replace(/_/g, " ");
  return [
    roomAssignment,
    item.count ? itx("c1.ui.value1_Stuck", "{{value1}} Stück", { "{{value1}}": String(item.count) }) : "",
    details.itemCount ? itx("c1.ui.value1_Stuck", "{{value1}} Stück", { "{{value1}}": String(details.itemCount) }) : "",
    details.channelType ? C1_ELO_LIGHT_TYPE_LABELS[details.channelType] ?? details.channelType : "",
    details.isDali ? "DALI" : "",
    details.lightProductId ? getLightProductDescription(details.lightProductId) : "",
    details.plugType ? C1_ELO_PLUG_TYPE_LABELS[details.plugType] ?? details.plugType : "",
    details.voltage ? C1_ELO_VOLTAGE_LABELS[details.voltage] ?? details.voltage : "",
    details.numberOfButtonChannels ? itx("c1.ui.value1_Kanale", "{{value1}} Kanäle", { "{{value1}}": String(details.numberOfButtonChannels) }) : "",
    details.buttonType ? getButtonProductDescription(details.buttonType) : "",
    details.sensorTypes?.map(type => C1_ELO_SENSOR_TYPE_LABELS[type] ?? type).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
}

function pdfButton(item: RoombookItem): C1_RaumbuchPdfButton | undefined {
  if (item.itemType !== C1_EloItem_Type.Button) return undefined;
  const button = c1_state.objectRegistry.getObject<C1_Button>(item.id);
  if (!(button instanceof C1_Button)) return undefined;
  const baseProps = button.owner?.baseProps.buttonBaseProps;
  if (!baseProps) return undefined;
  return {
    productId: buttonUiTemplate(baseProps.type),
    properties: { ...baseProps, orientation: baseProps.horizontal ? "horizontal" as const : "vertical" as const, type: C1_EloItem_Type.Button },
    rows: getC1ButtonRows(button),
  };
}

function roombookButtonView(item: RoombookItem, technicalMode: boolean): ReactNode {
  if (item.itemType === C1_EloItem_Type.Light) {
    const eloItem = c1_state.objectRegistry.getObject<C1_Consumer>(item.id);
    if (!(eloItem instanceof C1_Consumer)) return detailText(item);
    const imageUrl = getLightProductImageUrl(eloItem.lightProductId);
    if (!imageUrl) return detailText(item);
    return (
      <button
        type="button"
        className="C1_roombookLightView"
        title={itx("c1.ui.Leuchte_bearbeiten", "Leuchte bearbeiten")}
        aria-label={`${item.description || itx("components.Light", "Leuchte")} bearbeiten`}
        onClick={() => {
          c1_state.setSelection(eloItem);
          state.dockManager.openLightEditor(eloItem);
        }}
      >
        {technicalMode && <code>{detailText(item)}</code>}
        <img src={imageUrl} alt={getLightProductDescription(eloItem.lightProductId)} />
      </button>
    );
  }
  if (item.itemType !== C1_EloItem_Type.Button) return detailText(item);
  const eloItem = c1_state.objectRegistry.getObject<C1_Button>(item.id);
  const properties = eloItem?.owner?.baseProps.buttonBaseProps;
  if (!(eloItem instanceof C1_Button) || !properties) return detailText(item);

  return (
    <button
      type="button"
      className={`C1_roombookButtonView${technicalMode ? " C1_roombookButtonView--technical" : ""}`}
      title={itx("c1.ui.Taster_bearbeiten", "Taster bearbeiten")}
      aria-label={`${item.description || itx("menubar.buttons", "Taster")} bearbeiten`}
      onClick={() => {
        c1_state.setSelection(eloItem);
        state.dockManager.openButtonEditor(eloItem);
      }}
    >
      <span>{detailText(item)}</span>
      <C1_ButtonView
        eloItem={eloItem}
        width={properties.widthMm}
        height={properties.heightMm}
        color={properties.color ?? "white"}
        hasDisplay={properties.hasDisplay ?? false}
      />
    </button>
  );
}

function categoriesForRoom(
  consumers: C1_ReportConsumer[],
  buttons: C1_ReportButton[],
  sensors: C1_ReportConsumer[] = [],
): RoombookCategory[] {
  const items: RoombookItem[] = compactLightGroupRows([
    ...buttons.map((button) => ({ ...button, itemType: C1_EloItem_Type.Button as const })),
    ...sensors.map((sensor) => ({ ...sensor, itemType: C1_EloItem_Type.Sensor as const })),
    ...consumers.map((consumer) => ({ ...consumer, itemType: consumer.consumerType })),
  ]);
  const take = (id: string, title: string, predicate: (item: RoombookItem) => boolean): RoombookCategory => ({
    id,
    title,
    items: items.filter(predicate),
  });
  const knownTypes = new Set<C1_EloItem_Type | "window_shading">([
    C1_EloItem_Type.Button,
    C1_EloItem_Type.Sensor,
    C1_EloItem_Type.Panel,
    C1_EloItem_Type.Light,
    C1_EloItem_Type.Blind,
    C1_EloItem_Type.Jalousie,
    "window_shading",
    C1_EloItem_Type.WindowContact,
    C1_EloItem_Type.DoorLock,
    C1_EloItem_Type.Thermostat,
    C1_EloItem_Type.Heating,
    C1_EloItem_Type.Plug,
    C1_EloItem_Type.FixedPower,
  ]);

  return [
    take(
      "buttons",
      itx("menubar.buttons", "Taster"),
      (item) => item.itemType === C1_EloItem_Type.Button || item.itemType === C1_EloItem_Type.Panel,
    ),
    take("sensors", itx("components.plural.Sensor", "Sensoren"), (item) => item.itemType === C1_EloItem_Type.Sensor),
    take("lights", itx("components.plural.Light", "Leuchten"), (item) => item.itemType === C1_EloItem_Type.Light),
    take(
      "shading",
      itx("raumbuch.shade", "Beschattung"),
      (item) =>
        item.itemType === "window_shading" ||
        item.itemType === C1_EloItem_Type.Blind ||
        item.itemType === C1_EloItem_Type.Jalousie,
    ),
    take(
      "contacts",
      itx("components.plural.Lock", "Kontakte"),
      (item) => item.itemType === C1_EloItem_Type.WindowContact || item.itemType === C1_EloItem_Type.DoorLock,
    ),
    take(
      "heating",
      itx("components.plural.Heating", "Heizungen"),
      (item) => item.itemType === C1_EloItem_Type.Thermostat || item.itemType === C1_EloItem_Type.Heating,
    ),
    take("plugs", itx("components.plural.Outlet", "Steckdosen"), (item) => item.itemType === C1_EloItem_Type.Plug),
    take("fixed-power", "Feste Stromversorgung", (item) => item.itemType === C1_EloItem_Type.FixedPower),
    take("other", "Weitere Elektroobjekte", (item) => !knownTypes.has(item.itemType)),
  ].filter((category) => category.items.length > 0);
}

function sourcesForRoom(room: C1_Room, language: "de" | "en") {
  const roomDefinitions = getRoomEloItemProfile(room.type);
  const roomSource: Source = {
    id: room.id,
    // Der Wert ist editierbar und muss deshalb dem persistierten Raumnamen
    // entsprechen, nicht einer eventuell formatierten Displaydarstellung.
    title: (room.owner?.name ?? ""),
    rootDefinitionId: "room",
    definitions: roomDefinitions,
    instance: room.eloTree,
    object: { type: "room", value: room },
    onChange: (instance) => room.setEloTree(instance),
    onNameChange: (name) => (room.owner && (room.owner.name = name ?? "")),
  };
  const furnitureSources = room.furnitures.flatMap((furniture: C1_Furniture) => {
    const definition = furnitureById[furniture.definitionId];
    const definitions = getEloItemProfileForInstance(furniture.definitionId, furniture.eloTree);
    if (!definition || (!definitions.length && !isLightGroupDefinitionId(furniture.definitionId))) return [];
    return [
      {
        id: furniture.id,
        title: (furniture.owner?.displayName ?? ""),
        masterIndex: furniture.masterIndex,
        rootDefinitionId: furniture.definitionId,
        definitions,
        instance: furniture.eloTree,
        object: { type: "furniture" as const, value: furniture },
        onChange: (instance: C1_EloItem_Data) => furniture.setEloTree(instance),
        onNameChange: (name?: string) => { if (furniture.owner) furniture.owner.name = name ?? ""; },
      },
    ];
  });
  const openingSources = room.openings.flatMap((opening) => {
    const profile = getOpeningEloProfile(opening);
    const definitions = profile.definitions;
    if (!definitions.length) return [];
    return [
      {
        id: opening.id,
        title: (opening.owner?.displayName ?? ""),
        masterIndex: opening.masterIndex,
        rootDefinitionId: profile.rootDefinitionId,
        definitions,
        instance: opening.eloTree,
        object: { type: "opening" as const, value: opening as C1_Opening },
        onChange: (instance: C1_EloItem_Data) => opening.setEloTree(instance),
        onNameChange: (name?: string) => { if (opening.owner) opening.owner.name = name ?? ""; },
      },
    ];
  });
  return [roomSource, ...furnitureSources, ...openingSources];
}

function roombookRoomNameKey(name: string): string {
  return name.trim().normalize("NFC").toLocaleLowerCase("de");
}

function logicalModelRooms(floor: C1_Floor, roomName: string, language: "de" | "en"): C1_Room[] {
  const nameKey = roombookRoomNameKey(roomName);
  return floor.rooms.filter((room) => roombookRoomNameKey((room.owner?.displayName ?? "")) === nameKey);
}

function itemsForContainer(
  consumers: C1_ReportConsumer[],
  buttons: C1_ReportButton[],
  sensors: C1_ReportConsumer[] = [],
): RoombookItem[] {
  return [
    ...buttons.map((button) => ({ ...button, itemType: C1_EloItem_Type.Button as const })),
    ...sensors.map((sensor) => ({ ...sensor, itemType: C1_EloItem_Type.Sensor as const })),
    ...consumers.map((consumer) => ({ ...consumer, itemType: consumer.consumerType })),
  ];
}

function plugGroupSlots(group: C1_ReportGroup): WallOutletSlotType[] | undefined {
  const definitionId = group.definitionId?.replace(/-\d+$/, "");
  if (!definitionId || C1_eloItemsRegistry.get(definitionId)?.type !== C1_EloItem_Type.PlugGroup) return undefined;

  const slotType = (plugType: C1_EloItem_PlugType | undefined): WallOutletSlotType => {
    if (plugType === C1_EloItem_PlugType.LAN) return "lan";
    if (plugType === C1_EloItem_PlugType.LAN2) return "lan2";
    if (plugType === C1_EloItem_PlugType.TV) return "tv";
    if (plugType === C1_EloItem_PlugType.USB) return "usb";
    if (plugType?.startsWith(C1_EloItem_PlugType.V230Switch)) return "schukoSwitched";
    return "schuko";
  };

  return group.consumers
    .filter((consumer) => consumer.consumerType === C1_EloItem_Type.Plug)
    .map((consumer) =>
      slotType(consumer.details && "plugType" in consumer.details ? consumer.details.plugType : undefined),
    );
}

function groupedPdfCategories(
  modelRooms: C1_Room[],
  transformedRoom: C1_ReportRoom,
  language: "de" | "en",
): C1_RaumbuchPdfRoom["categories"] {
  const categories: C1_RaumbuchPdfRoom["categories"] = [];
  const sources = modelRooms
    .flatMap((room) => sourcesForRoom(room, language))
    .sort(
      (left, right) =>
        (left.masterIndex ?? Number.MAX_SAFE_INTEGER) - (right.masterIndex ?? Number.MAX_SAFE_INTEGER) ||
        left.title.localeCompare(right.title, language, { numeric: true, sensitivity: "base" }),
    );
  const pdfItems = (items: RoombookItem[]) =>
    compactLightGroupRows(items).map((item) => ({
      tag: item.tag || "—",
      type: typeLabel(item.itemType),
      name: item.description || "",
      details: detailText(item),
      button: pdfButton(item),
      consumerImage: roombookConsumerImage(item),
    }));
  const eloGroupCategories = (
    group: C1_ReportGroup,
    sourceId: string,
    sourceTitle: string,
    level: number,
  ): C1_RaumbuchPdfRoom["categories"] => {
    const title = group.name || group.description || itx("components.group", "Gruppe");
    const items = pdfItems(
      itemsForContainer(
        group.consumers.filter((item) => item.sourceId === sourceId),
        group.buttons.filter((item) => item.sourceId === sourceId),
      ),
    );
    const redundantWrapper =
      items.length === 0 && title.localeCompare(sourceTitle, language, { sensitivity: "base" }) === 0;
    const children = group.groups
      .filter((item) => item.sourceId === sourceId)
      .flatMap((child) => eloGroupCategories(child, sourceId, sourceTitle, redundantWrapper ? level : level + 1));
    if (items.length === 0 && children.length === 0) return [];
    if (redundantWrapper) return children;
    return [{ title, level, outletSlots: plugGroupSlots(group), items }, ...children];
  };

  sources.forEach((source) => {
    const sourceGroups = transformedRoom.groups.filter((group) => group.sourceId === source.id);
    const sourceWrapper = sourceGroups.find((group) => group.id === source.id);
    const childGroups = sourceWrapper ? sourceWrapper.groups : sourceGroups;
    const directItems = sourceWrapper
      ? itemsForContainer(sourceWrapper.consumers, sourceWrapper.buttons)
      : sourceGroups.length > 0
        ? []
        : itemsForContainer(
            transformedRoom.consumers.filter((item) => item.sourceId === source.id),
            transformedRoom.buttons.filter((item) => item.sourceId === source.id),
            transformedRoom.sensors.filter((item) => item.sourceId === source.id),
          );
    categories.push({
      title: `${source.masterIndex !== undefined ? `#${source.masterIndex} · ` : ""}${source.title}`,
      details: c1RoombookObjectDetailsText(source.object, language),
      items: pdfItems(directItems),
    });
    childGroups.forEach((group) => categories.push(...eloGroupCategories(group, source.id, source.title, 1)));
  });
  return categories;
}

export async function downloadCompleteC1RaumbuchPdf(
  requestedMode?: "functions" | "groups",
  download = true,
  projectName?: string,
): Promise<C1_PdfChapterArtifact | undefined> {
  if (state.projectMetadata.isC1Project === false) {
    const { createClassicRoombookPdf } = await import("../../../src/pdf/createClassicRoombookPdf");
    return createClassicRoombookPdf(state, projectName ?? state.projectMetadata.name, download);
  }
  projectName ??= c1_state.project.projectName;
  const mode = requestedMode ?? c1_state.roombookViewMode.value;
  const { rooms, summary } = collectC1RoombookForExport(mode);
  if (rooms.length === 0) return;
  return await downloadC1RaumbuchPdf({ projectName,
    dateText: new Intl.DateTimeFormat(state.settings.uiLanguage).format(new Date()),
    mode, rooms, summary }, download, !download);
}

/** Shared immutable room snapshot for PDF and Excel documentation. */
export function collectC1RoombookForExport(mode: "functions" | "groups") {
  const project = c1_state.project;
  const language = c1_state.language;
  const transformedProject = createC1ProjectReportData(project);
  const projectSummary = createC1RoombookProjectSummary(transformedProject);
  const rooms: C1_RaumbuchPdfRoom[] = transformedProject.floors
    .flatMap((floor) => floor.rooms.map((room) => ({ floor, room })))
    .sort(
      (left, right) =>
        comparePdfLabels(left.floor, right.floor, language) || comparePdfLabels(left.room, right.room, language),
    )
    .map(({ floor, room }) => ({
      id: room.id,
      floorLabel: pdfHeaderText(floor),
      roomLabel: pdfHeaderText(room),
      fullTag: room.fullTag ?? "",
      floorTag: floor.tag,
      roomTag: room.tag,
      floorName: floor.name,
      roomName: room.name,
      categories:
        mode === "groups"
          ? groupedPdfCategories(
              logicalModelRooms(project.floors.find((itemFloor) => itemFloor.id === floor.id)!, room.name, language),
              room,
              language,
            )
          : categoriesForRoom(room.consumers, room.buttons, room.sensors).map((category) => ({
              title: category.title,
              items: category.items.map((item) => ({
                tag: item.tag || "—",
                name: item.description || "",
                details: detailText(item),
                button: pdfButton(item),
                consumerImage: roombookConsumerImage(item),
              })),
            })),
    }));

  return { rooms, summary: projectSummary };
}

export function C1_Raumbuch() {
  useOnChangeWatcher(state.settings);
  // Temporary component-level feature switch. Set to false to restore the previous single-room view.
  const showAllRooms = true;
  useOnChangeWatcher(uiModeWatcher);
  useOnChangeWatcher(c1_state.project.displayMode);
  const technicalMode = c1_state.project.displayMode.value === "technical";
  const project = c1_state.project;
  const language = c1_state.language;
  const [roomId, setRoomId] = useState(project.floors[0]?.rooms[0]?.id ?? "");
  const [expanded, setExpanded] = useState(() => new Set(project.floors.map((floor) => floor.id)));
  const [editingSourceId, setEditingSourceId] = useState<string>();
  const viewMode = c1_state.roombookViewMode.value;
  const editorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const room = project.floors.flatMap((floor) => floor.rooms).find((candidate) => candidate.id === roomId);
  const floor = project.floors.find((candidate) =>
    candidate.rooms.some((candidateRoom) => candidateRoom.id === roomId),
  );
  const sources = room ? sourcesForRoom(room, language) : [];
  const allSources = project.floors.flatMap((itemFloor) =>
    itemFloor.rooms.flatMap((itemRoom) => sourcesForRoom(itemRoom, language)),
  );
  const transformedProject = createC1ProjectReportData(project);
  const compareLabel = (left: { tag?: string; name?: string; owner?: {tag: string; name: string} }, right: { tag?: string; name?: string; owner?: {tag: string; name: string} }) =>
    compareTaggedLabels(left.owner ?? left, right.owner ?? right, language);
  const sortedProjectFloors = [...project.floors]
    .sort(compareLabel)
    .map((itemFloor) => ({
      floor: itemFloor,
      rooms: [...itemFloor.rooms]
        .sort(compareLabel)
        .filter(
          (itemRoom, index, rooms) =>
            rooms.findIndex(
              (candidate) =>
                roombookRoomNameKey((candidate.owner?.displayName ?? "")) ===
                roombookRoomNameKey((itemRoom.owner?.displayName ?? "")),
            ) === index,
        ),
    }));
  const sortedTransformedFloors = [...transformedProject.floors].sort(compareLabel).map((itemFloor) => ({
    ...itemFloor,
    rooms: [...itemFloor.rooms].sort(compareLabel),
  }));
  const projectSummary = createC1RoombookProjectSummary({
    ...transformedProject,
    floors: sortedTransformedFloors,
  });
  const summaryColumns = visibleC1RoombookSummaryColumns(projectSummary);
  const summaryColumnGroups = summaryColumns.reduce<Array<{ label: string; count: number }>>((groups, column) => {
    const last = groups.at(-1);
    if (last?.label === column.group) last.count += 1;
    else groups.push({ label: column.group, count: 1 });
    return groups;
  }, []);
  const transformedRoom = transformedProject.floors
    .flatMap((itemFloor) => itemFloor.rooms)
    .find((candidate) => candidate.id === roomId);
  const activeSource = (showAllRooms ? allSources : sources).find((source) => source.id === editingSourceId);
  const sourceById: Map<string, Source> = new Map(sources.map((source) => [source.id, source]));
  const renderRows = (items: RoombookItem[], keyPrefix: string, withSource: boolean, roomSourceById = sourceById) => (
    <div className={`C1_roombookTable${withSource ? "" : " C1_roombookTable--withoutSource"}`}>
      <div className="head">
        <span>{itx("ColumnFilterUI.tag", "Tag").toUpperCase()}</span>
        {!withSource && <span>{itx("type", "Typ").toUpperCase()}</span>}
        <span>{itx("name", "Name").toUpperCase()}</span>
        <span>{itx("c1.ui.AUSFUHRUNG", "AUSFÜHRUNG")}</span>
        {withSource && <span>{itx("c1.ui.QUELLE", "QUELLE")}</span>}
      </div>
      {compactLightGroupRows(items).map((item) => {
        const source = item.sourceId ? roomSourceById.get(item.sourceId) : undefined;
        const consumerImage = roombookConsumerImage(item);
        return (
          <div key={`${keyPrefix}-${item.id}`}>
            <b className="C1_roombookConsumerTag">
              {consumerImage && <img src={consumerImage} alt="" />}
              {item.tag || "—"}
            </b>
            {!withSource && <span>{typeLabel(item.itemType)}</span>}
            <span>{item.description}</span>
            <span className={item.itemType === C1_EloItem_Type.Button ? "C1_roombookButtonCell" : undefined}>
              {roombookButtonView(item, technicalMode)}
            </span>
            {withSource &&
              (source ? (
                <button
                  type="button"
                  className="C1_roombookRowEdit"
                  title={`${source.title} bearbeiten`}
                  aria-label={`${source.title} bearbeiten`}
                  onClick={() => setEditingSourceId(source.id)}
                >
                  <Pencil />
                  {source.masterIndex !== undefined && (
                    <span className="C1_roombookSourceIndex">{source.masterIndex}</span>
                  )}
                  {source.title}
                </button>
              ) : (
                <code>{typeLabel(item.itemType)}</code>
              ))}
          </div>
        );
      })}
    </div>
  );

  const renderEloGroup = (
    group: C1_ReportGroup,
    sourceId: string,
    sourceTitle: string,
    path: string,
    roomSourceById: Map<string, Source>,
  ): ReactNode => {
    const items = itemsForContainer(
      group.consumers.filter((item) => item.sourceId === sourceId),
      group.buttons.filter((item) => item.sourceId === sourceId),
    );
    const childGroups = group.groups.filter((item) => item.sourceId === sourceId);
    const renderedChildren = childGroups
      .map((child) => renderEloGroup(child, sourceId, sourceTitle, `${path}-${group.id}`, roomSourceById))
      .filter(Boolean);
    const title = group.name || group.description || itx("components.group", "Gruppe");
    const outletSlots = plugGroupSlots(group);
    if (items.length === 0 && renderedChildren.length === 0) return null;
    if (items.length === 0 && title.localeCompare(sourceTitle, language, { sensitivity: "base" }) === 0) {
      return renderedChildren;
    }
    return (
      <div className="C1_roombookEloGroup" key={`${path}-${group.id}`}>
        <div className="C1_roombookEloGroupHead">
          <b>{title}</b>
          {group.tag && <span>{group.tag}</span>}
          {outletSlots && outletSlots.length > 0 && (
            <WallOutletSvg className="C1_roombookPlugGroupPreview" slots={outletSlots} />
          )}
        </div>
        {items.length > 0 && renderRows(items, `${path}-${group.id}`, false, roomSourceById)}
        {renderedChildren}
      </div>
    );
  };

  const renderGroupedRoomDocument = (itemFloor: C1_ReportFloor, itemRoom: C1_ReportRoom) => {
    const modelFloor = project.floors.find((candidateFloor) => candidateFloor.id === itemFloor.id);
    const roomSources = modelFloor
      ? logicalModelRooms(modelFloor, itemRoom.name, language).flatMap((modelRoom) =>
          sourcesForRoom(modelRoom, language),
        )
      : [];
    const roomSourceById = new Map(roomSources.map((source) => [source.id, source]));
    const sortedSources = [...roomSources].sort(
      (left, right) =>
        (left.masterIndex ?? Number.MAX_SAFE_INTEGER) - (right.masterIndex ?? Number.MAX_SAFE_INTEGER) ||
        left.title.localeCompare(right.title, language, { numeric: true, sensitivity: "base" }),
    );

    return (
      <section
        className="C1_roombookDocument"
        key={`${itemFloor.id}-${itemRoom.id}-groups`}
        data-c1-roombook-room-id={itemRoom.id}
      >
        <header className="C1_roombookTitle">
          <div className="C1_roombookRoomTitle">
            <h1>
              <span className="C1_roombookFloorTitle">
                {[itemFloor.tag, itemFloor.name].filter(Boolean).join(" · ")}
              </span>
              <span className="C1_roombookRoomTitleText">
                {[itemRoom.tag, itemRoom.name].filter(Boolean).join(" · ")}
              </span>
            </h1>
          </div>
        </header>
        <div className="C1_roombookSheet">
          {sortedSources.length > 0 ? (
            <div className="C1_roombookGroupedSources C1_roombookGroupedSources--root">
              {sortedSources.map((source) => {
                const sourceGroups = itemRoom.groups.filter((group) => group.sourceId === source.id);
                const sourceWrapper = sourceGroups.find((group) => group.id === source.id);
                const eloGroups = sourceWrapper ? sourceWrapper.groups : sourceGroups;
                const directItems = sourceWrapper
                  ? itemsForContainer(sourceWrapper.consumers, sourceWrapper.buttons)
                  : sourceGroups.length > 0
                    ? []
                    : itemsForContainer(
                        itemRoom.consumers.filter((item) => item.sourceId === source.id),
                        itemRoom.buttons.filter((item) => item.sourceId === source.id),
                        itemRoom.sensors.filter((item) => item.sourceId === source.id),
                      );
                const objectDetails = c1RoombookObjectDetailsText(source.object, language);
                return (
                  <section className="C1_roombookGroupedSource" key={source.id}>
                    <div className="C1_roombookGroupedSourceHead">
                      <div className="C1_roombookGroupedSourceIdentity">
                        <div className="C1_roombookGroupedSourceName">
                          {source.masterIndex !== undefined && (
                            <span className="C1_roombookGroupedSourceIndex">#{source.masterIndex} ·</span>
                          )}
                          <input
                            key={`${source.id}-${source.title}`}
                            type="text"
                            defaultValue={source.title}
                            aria-label={itx("c1.ui.Name_fur_value1", "Name für {{value1}}", { "{{value1}}": String(source.title) })}
                            onChange={(event) => {
                              event.currentTarget.dataset.edited = "true";
                            }}
                            onBlur={(event) => {
                              if (event.currentTarget.dataset.edited !== "true") return;
                              source.onNameChange(event.currentTarget.value.trim());
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") event.currentTarget.blur();
                            }}
                          />
                          {objectDetails && <span className="C1_roombookGroupedSourceDetails">{objectDetails}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        title={`${source.title} bearbeiten`}
                        onClick={() => setEditingSourceId(source.id)}
                      >
                        <Pencil />
                        {itx("edit", "Bearbeiten")} </button>
                    </div>
                    {directItems.length > 0 && renderRows(directItems, source.id, false, roomSourceById)}
                    {eloGroups.map((group) =>
                      renderEloGroup(group, source.id, source.title, source.id, roomSourceById),
                    )}
                    {directItems.length === 0 && eloGroups.length === 0 && (
                      <div className="C1_roombookGroupEmpty">{itx("c1.ui.Keine_ELO_Items_konfiguriert", "Keine ELO-Items konfiguriert.")}</div>
                    )}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="C1_roombookEmpty">{itx("c1.ui.Fur_diesen_Raum_sind_noch_keine_Gruppen_definiert", "Für diesen Raum sind noch keine Gruppen definiert.")}</div>
          )}
        </div>
      </section>
    );
  };

  const renderRoomDocument = (itemFloor: C1_ReportFloor, itemRoom: C1_ReportRoom) => {
    const roomCategories = categoriesForRoom(itemRoom.consumers, itemRoom.buttons, itemRoom.sensors);
    const modelFloor = project.floors.find((candidateFloor) => candidateFloor.id === itemFloor.id);
    const roomSourceById = new Map(
      (modelFloor
        ? logicalModelRooms(modelFloor, itemRoom.name, language).flatMap((modelRoom) =>
            sourcesForRoom(modelRoom, language),
          )
        : []
      ).map((source) => [source.id, source]),
    );
    return (
      <section
        className="C1_roombookDocument"
        key={`${itemFloor.id}-${itemRoom.id}`}
        data-c1-roombook-room-id={itemRoom.id}
      >
        <header className="C1_roombookTitle">
          <div className="C1_roombookRoomTitle">
            <h1>
              <span className="C1_roombookFloorTitle">
                {[itemFloor.tag, itemFloor.name].filter(Boolean).join(" · ")}
              </span>
              <span className="C1_roombookRoomTitleText">
                {[itemRoom.tag, itemRoom.name].filter(Boolean).join(" · ")}
              </span>
            </h1>
          </div>
        </header>
        <div className="C1_roombookSheet">
          {roomCategories.length === 0 ? (
            <div className="C1_roombookEmpty">
              {itx("c1.ui.Fur_diesen_Raum_sind_noch_keine_elektrischen_Ausstattungsobjekte_definiert", "Für diesen Raum sind noch keine elektrischen Ausstattungsobjekte definiert.")} </div>
          ) : (
            roomCategories.map((category) => (
              <article className="C1_roombookSource" key={category.id}>
                <div className="C1_roombookSourceHead">
                  <div>
                    <b>{category.title}</b>
                  </div>
                </div>
                {renderRows(category.items, `${itemRoom.id}-${category.id}`, true, roomSourceById)}
              </article>
            ))
          )}
        </div>
      </section>
    );
  };

  const renderSummaryRow = (summaryRow: C1_RoombookSummaryRow, total = false) => (
    <tr key={summaryRow.id} className={total ? "C1_roombookSummaryTotal" : undefined}>
      <th scope="row">{summaryRow.label}</th>
      {summaryColumns.map((column) => (
        <td key={column.id}>{formatC1RoombookSummaryValue(summaryRow, column)}</td>
      ))}
      <td>{summaryRow.total || "–"}</td>
    </tr>
  );

  const renderSummaryHeader = (firstLabel: string) => (
    <thead>
      <tr>
        <th scope="col" rowSpan={2}>
          {firstLabel}
        </th>
        {summaryColumnGroups.map((group) => (
          <th scope="colgroup" colSpan={group.count} key={group.label}>
            {group.label}
          </th>
        ))}
        <th scope="col" rowSpan={2}>
          {itx("templatePriceList.summ", "Summe")} </th>
      </tr>
      <tr>
        {summaryColumns.map((column) => (
          <th scope="col" key={column.id}>
            {column.label || itx("count", "Anzahl")}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderSummaryDocument = () => (
    <section className="C1_roombookDocument C1_roombookSummaryDocument" key="summary" data-c1-roombook-summary>
      <header className="C1_roombookTitle">
        <div className="C1_roombookRoomTitle">
          <h1>
            <span className="C1_roombookFloorTitle">{itx("pageDocumentType.roombook", "Raumbuch")}</span>
            <span className="C1_roombookRoomTitleText">{itx("TopologyLineView.summary", "Zusammenfassung")}</span>
          </h1>
        </div>
      </header>
      <div className="C1_roombookSheet C1_roombookSummarySheet">
        {projectSummary.floors.map((floorSummary) => (
          <section className="C1_roombookSummaryFloor" key={floorSummary.id}>
            <h2>{floorSummary.label}</h2>
            <div className="C1_roombookSummaryTableScroll">
              <table className="C1_roombookSummaryTable">
                {renderSummaryHeader(itx("cabinetData.room", "Raum"))}
                <tbody>
                  {floorSummary.rows.map((summaryRow) => renderSummaryRow(summaryRow))}
                  {renderSummaryRow(floorSummary.total, true)}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        <section className="C1_roombookSummaryProject">
          <h2>{itx("cover.project", "Projekt")}</h2>
          <div className="C1_roombookSummaryTableScroll">
            <table className="C1_roombookSummaryTable">
              {renderSummaryHeader(itx("ConfigList.total", "Gesamt"))}
              <tbody>{renderSummaryRow(projectSummary.total, true)}</tbody>
            </table>
          </div>
        </section>
        {hasC1RoombookSummaryWiringCounts(projectSummary) && (
          <p className="C1_roombookSummaryExplanation">{getC1RoombookWiringCountExplanation()}</p>
        )}
      </div>
    </section>
  );

  useEffect(() => {
    if (!activeSource) return;
    const closeEditorOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !editorRef.current?.contains(event.target)) {
        setEditingSourceId(undefined);
      }
    };
    window.addEventListener("pointerdown", closeEditorOutside);
    return () => window.removeEventListener("pointerdown", closeEditorOutside);
  }, [activeSource]);

  useEffect(() => {
    if (!showAllRooms) return;
    const content = contentRef.current;
    if (!content) return;
    let animationFrame = 0;
    const updateActiveRoom = () => {
      animationFrame = 0;
      const documents = [...content.querySelectorAll<HTMLElement>("[data-c1-roombook-room-id]")];
      if (!documents.length) return;
      const referenceTop = content.getBoundingClientRect().top + 80;
      const atBottom = content.scrollHeight - content.scrollTop - content.clientHeight < 2;
      const active = atBottom
        ? documents[documents.length - 1]
        : ([...documents].reverse().find((document) => document.getBoundingClientRect().top <= referenceTop) ??
          documents[0]);
      const nextRoomId = active.dataset.c1RoombookRoomId;
      if (nextRoomId) setRoomId((current) => (current === nextRoomId ? current : nextRoomId));
    };
    const onScroll = () => {
      if (!animationFrame) animationFrame = requestAnimationFrame(updateActiveRoom);
    };
    content.addEventListener("scroll", onScroll, { passive: true });
    updateActiveRoom();
    return () => {
      content.removeEventListener("scroll", onScroll);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [showAllRooms, viewMode]);

  return (
    <section className="C1_roombook">
      <aside className="C1_roombookNav">
        <div className="C1_roombookNavHead">
          <span>{itx("c1.ui.GEBAUDESTRUKTUR", "GEBÄUDESTRUKTUR")}</span>
          <small>{sortedProjectFloors.reduce((sum, item) => sum + item.rooms.length, 0)}  {itx("etsImportConfig.rooms", "Räume")}</small>
        </div>
        <C1_RoombookViewModeSelect
          value={viewMode}
          onChange={(mode) => {
            c1_state.roombookViewMode.value = mode;
          }}
        />
        {sortedProjectFloors.map(({ floor: itemFloor, rooms: sortedRooms }) => (
          <div key={itemFloor.id}>
            <button
              type="button"
              className="C1_roombookFloor"
              onClick={() =>
                setExpanded((current) => {
                  const next = new Set(current);
                  if (next.has(itemFloor.id)) next.delete(itemFloor.id);
                  else next.add(itemFloor.id);
                  return next;
                })
              }
            >
              {expanded.has(itemFloor.id) ? <ChevronDown /> : <ChevronRight />}
              <b>{(itemFloor.owner?.name ?? "")}</b>
              <small>{sortedRooms.length}</small>
            </button>
            {expanded.has(itemFloor.id) &&
              sortedRooms.map((itemRoom) => (
                <button
                  type="button"
                  key={itemRoom.id}
                  className={`C1_roombookRoom${itemRoom.id === roomId ? " active" : ""}`}
                  onClick={() => {
                    setRoomId(itemRoom.id);
                    setEditingSourceId(undefined);
                    if (showAllRooms) {
                      contentRef.current
                        ?.querySelector<HTMLElement>(`[data-c1-roombook-room-id="${itemRoom.id}"]`)
                        ?.scrollIntoView({ behavior: "auto", block: "start" });
                    }
                  }}
                >
                  <span>{itemRoom.owner?.tag || "—"}</span>
                  <b>{(itemRoom.owner?.name ?? "")}</b>
                </button>
              ))}
          </div>
        ))}
        <button
          type="button"
          className="C1_roombookFloor C1_roombookSummaryNav"
          onClick={() => {
            setEditingSourceId(undefined);
            contentRef.current
              ?.querySelector<HTMLElement>("[data-c1-roombook-summary]")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          <Sigma />
          <b>{itx("TopologyLineView.summary", "Zusammenfassung")}</b>
        </button>
      </aside>

      <div className={`C1_roombookContent${showAllRooms ? " C1_roombookContent--allRooms" : ""}`} ref={contentRef}>
        {!room || !floor ? (
          <p>{itx("c1.ui.Das_Projekt_enthalt_noch_keine_Raume", "Das Projekt enthält noch keine Räume.")}</p>
        ) : showAllRooms ? (
          <>
            {sortedTransformedFloors.flatMap((itemFloor) =>
              itemFloor.rooms.map((itemRoom) =>
                viewMode === "groups"
                  ? renderGroupedRoomDocument(itemFloor, itemRoom)
                  : renderRoomDocument(itemFloor, itemRoom),
              ),
            )}
            {renderSummaryDocument()}
          </>
        ) : transformedRoom ? (
          viewMode === "groups" ? (
            renderGroupedRoomDocument(
              transformedProject.floors.find((itemFloor) =>
                itemFloor.rooms.some((itemRoom) => itemRoom.id === transformedRoom.id),
              )!,
              transformedRoom,
            )
          ) : (
            renderRoomDocument(
              transformedProject.floors.find((itemFloor) =>
                itemFloor.rooms.some((itemRoom) => itemRoom.id === transformedRoom.id),
              )!,
              transformedRoom,
            )
          )
        ) : (
          <p>{itx("c1.ui.Der_ausgewahlte_Raum_konnte_nicht_geladen_werden", "Der ausgewählte Raum konnte nicht geladen werden.")}</p>
        )}
      </div>

      {activeSource && (
        <div className="C1_roombookEditor" ref={editorRef}>
          <div className="C1_roombookEditorHead">
            <div>
              <h2>{activeSource.title}</h2>
            </div>
          </div>
          <div className="C1_roombookEditorBody">
            <C1_RaumbuchObjectDetails object={activeSource.object} />
            <C1_EloEquipmentEditor
              key={activeSource.rootDefinitionId}
              state={c1_state}
              selectedItemId={c1_state.selection?.id}
              collapsible={false}
              rootDefinitionId={activeSource.rootDefinitionId}
              eloTree={activeSource.instance}
              eloDefinition={activeSource.definitions}
              onChange={(instance) => {
                activeSource.onChange(instance);
                state.undoManager.userInteractionEnded();
              }}
            />
          </div>
          <div className="C1_roombookEditorFoot">
            <button type="button" className="C1_primary" onClick={() => setEditingSourceId(undefined)}>
              {itx("inviteUserDialog.finished", "Fertig")} </button>
          </div>
        </div>
      )}
    </section>
  );
}
